import JoinCommunity from "../components/JoinCommunity/JoinCommunity";
import SharePage from "../components/SharePage/SharePage";
import ProductDescription from "../components/ProductDescription/ProductDescription";
import ProductField from "../components/ProductField/ProductField";
import ProductBreadcrumbs from "../components/ProductBreadcrumbs";

import { useMemo, useState, useEffect, useLayoutEffect, useRef } from "react";

import { useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchSingleProduct, fetchRelatedProducts } from "../store/slices/singleProductSlice";
import RelatedProducts from "../components/RelatedProducts/RelatedProducts";
import { fetchPurchasedProducts } from "../store/slices/profileProductSlice";
import Modal from "../components/Modal/Modal";
import ContacUs from "../components/ContacUs/ContacUs";
import { Helmet } from "react-helmet-async";
import styles from './Product.module.css';
import { API_ORIGIN } from '../config';
import { getLensDisplayName } from "../utils/lens";
import { buildProductAnalyticsProps, capture } from "../analytics";

const toAbsoluteUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return API_ORIGIN + url;
};

const Product = () => {

  const { singleProduct, status, error } = useSelector(state => state.singleProduct)
  const fetchError = error?.fetchProductError;
  const { isAuthenticated } = useSelector(state => state.user)
  const dispatch = useDispatch()
  const { productId } = useParams();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const headerSectionRef = useRef(null);
  const bgLayerRef = useRef(null);
  const rafRef = useRef(null);
  const relatedRevealRef = useRef(null);
  const joinRevealRef = useRef(null);
  const currentOffsetRef = useRef({ x: 0, y: 0 });
  const targetOffsetRef = useRef({ x: 0, y: 0 });
  const pointerOffsetRef = useRef({ x: 0, y: 0 });
  const scrollOffsetRef = useRef({ x: 0, y: 0 });
  const reduceMotionRef = useRef(false);
  const allowPointerParallaxRef = useRef(true);
  const viewedProductRef = useRef(null);

  const [activeButton, setActiveButton] = useState('Assets');

  const metadata = useMemo(() => {
    // Keyword-first title (lens + camera), matching the crawler render. Brand
    // lives in og:site_name. Serial is dropped here -- the SPA can't tell if a
    // camera+lens sibling exists, and the crawler render adds it when needed.
    const titleParts = [
      getLensDisplayName(singleProduct?.lens),
      singleProduct?.camera?.model_name,
    ].filter(Boolean);

    const baseTitle = titleParts.length
      ? `${titleParts.join(" ")} distortion grid`
      : "Distortion grid";

    const rawDescription = singleProduct?.description
      ? singleProduct.description.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
      : null;

    const description = rawDescription
      ? rawDescription.length > 160
        ? `${rawDescription.slice(0, 157)}...`
        : rawDescription
      : "Download calibrated distortion grids, presets, and assets tailored for your camera and lens from Matchmove machine.";

    return { title: baseTitle, description };
  }, [singleProduct]);

  useLayoutEffect(() => {
    const prev = document.body.style.background;
    document.body.style.background = '#111216';
    return () => { document.body.style.background = prev; };
  }, []);

  useEffect(() => {
    dispatch(fetchSingleProduct(productId));
    dispatch(fetchRelatedProducts({ slug: productId, pageSize: 12 }));
    if (isAuthenticated) {
      dispatch(fetchPurchasedProducts({ page: 1, pageSize: 100 }));
    }
  }, [dispatch, productId, isAuthenticated]);

  useEffect(() => {
    if (
      status.fetchProductStatus !== "succeeded" ||
      !singleProduct?.id ||
      viewedProductRef.current === singleProduct.id
    ) {
      return;
    }

    viewedProductRef.current = singleProduct.id;
    capture("product_viewed", buildProductAnalyticsProps(singleProduct));
  }, [status.fetchProductStatus, singleProduct]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const coarseQuery = window.matchMedia('(pointer: coarse)');

    const syncInteractionPrefs = () => {
      reduceMotionRef.current = motionQuery.matches;
      allowPointerParallaxRef.current = !coarseQuery.matches;
      if (motionQuery.matches || coarseQuery.matches) {
        targetOffsetRef.current = { x: 0, y: 0 };
      }
    };

    syncInteractionPrefs();

    motionQuery.addEventListener?.('change', syncInteractionPrefs);
    coarseQuery.addEventListener?.('change', syncInteractionPrefs);

    return () => {
      motionQuery.removeEventListener?.('change', syncInteractionPrefs);
      coarseQuery.removeEventListener?.('change', syncInteractionPrefs);
    };
  }, []);

  const updateParallaxTarget = () => {
    targetOffsetRef.current = {
      x: pointerOffsetRef.current.x + scrollOffsetRef.current.x,
      y: pointerOffsetRef.current.y + scrollOffsetRef.current.y,
    };
  };

  useEffect(() => {
    return () => {
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, []);

  const startParallaxFrame = () => {
    if (typeof window === 'undefined' || rafRef.current) return;

    const animate = () => {
      const bgLayerNode = bgLayerRef.current;
      if (!bgLayerNode) {
        rafRef.current = null;
        return;
      }

      const current = currentOffsetRef.current;
      const target = targetOffsetRef.current;
      const nextX = current.x + (target.x - current.x) * 0.09;
      const nextY = current.y + (target.y - current.y) * 0.09;

      currentOffsetRef.current = { x: nextX, y: nextY };
      bgLayerNode.style.setProperty('--hero-parallax-x', `${nextX.toFixed(2)}px`);
      bgLayerNode.style.setProperty('--hero-parallax-y', `${nextY.toFixed(2)}px`);

      const needsAnotherFrame =
        Math.abs(target.x - nextX) > 0.1 ||
        Math.abs(target.y - nextY) > 0.1 ||
        Math.abs(nextX) > 0.1 ||
        Math.abs(nextY) > 0.1;

      if (needsAnotherFrame) {
        rafRef.current = window.requestAnimationFrame(animate);
      } else {
        bgLayerNode.style.setProperty('--hero-parallax-x', '0px');
        bgLayerNode.style.setProperty('--hero-parallax-y', '0px');
        currentOffsetRef.current = { x: 0, y: 0 };
        rafRef.current = null;
      }
    };

    rafRef.current = window.requestAnimationFrame(animate);
  };

  const handleHeroPointerMove = (event) => {
    if (reduceMotionRef.current || !allowPointerParallaxRef.current) return;

    const headerNode = headerSectionRef.current;
    if (!headerNode) return;

    const rect = headerNode.getBoundingClientRect();
    const relativeX = (event.clientX - rect.left) / rect.width;
    const relativeY = (event.clientY - rect.top) / rect.height;

    pointerOffsetRef.current = {
      x: (relativeX - 0.5) * 3,
      y: (relativeY - 0.5) * 2,
    };

    updateParallaxTarget();
    startParallaxFrame();
  };

  const handleHeroPointerLeave = () => {
    pointerOffsetRef.current = { x: 0, y: 0 };
    updateParallaxTarget();
    startParallaxFrame();
  };

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const updateScrollParallax = () => {
      if (reduceMotionRef.current) return;

      const headerNode = headerSectionRef.current;
      if (!headerNode) return;

      const rect = headerNode.getBoundingClientRect();
      const viewportHeight = window.innerHeight || 1;
      const sectionCenter = rect.top + rect.height / 2;
      const viewportCenter = viewportHeight / 2;
      const normalized = Math.max(-1, Math.min(1, (sectionCenter - viewportCenter) / viewportHeight));

      scrollOffsetRef.current = {
        x: normalized * -0.75,
        y: normalized * -2,
      };

      updateParallaxTarget();
      startParallaxFrame();
    };

    updateScrollParallax();
    window.addEventListener('scroll', updateScrollParallax, { passive: true });
    window.addEventListener('resize', updateScrollParallax);

    return () => {
      window.removeEventListener('scroll', updateScrollParallax);
      window.removeEventListener('resize', updateScrollParallax);
    };
  }, []);

  const handleButtonClick = (buttonName) => {
    setActiveButton(buttonName);
  };

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);

  const isFailed = status.fetchProductStatus === "failed";
  const isSuccess = Boolean(singleProduct) && status.fetchProductStatus === "succeeded";
  const isLoading = !isSuccess && !isFailed; // covers "idle" + "loading"

  // Scroll-triggered reveal for below-fold sections (must be after isSuccess declaration)
  useEffect(() => {
    if (!isSuccess || typeof window === 'undefined' || !window.IntersectionObserver) return;

    const targets = [relatedRevealRef.current, joinRevealRef.current].filter(Boolean);
    if (!targets.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.dataset.revealed = 'true';
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );

    targets.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [isSuccess]);

  const returnTo = typeof location.state?.returnTo === "string"
    ? location.state.returnTo
    : "";

  const productTitle = isSuccess
    ? [
        getLensDisplayName(singleProduct.lens),
        singleProduct.camera?.model_name,
        singleProduct.serial_number,
      ].filter(Boolean).join(' ')
    : '';

  if (isFailed) {
    if (fetchError?.status === 401 || fetchError?.status === 403) return null;
    return (
      <section className="width">
        <Helmet>
          <title>{metadata.title}</title>
          <meta name="description" content={metadata.description} />
        </Helmet>
        <h2 className="h2-medium">Item not found or an error occurred.</h2>
        <p className="h4-medium">Please try again.</p>
      </section>
    );
  }

  return (
    <>
      <Helmet>
        <title>{metadata.title}</title>
        <meta name="description" content={metadata.description} />
        {isSuccess && <>
          <meta property="og:title" content={metadata.title} />
          <meta property="og:description" content={metadata.description} />
          <meta property="og:image" content="https://grids.matchmovemachine.com/preview.jpg" />
          <meta property="og:url" content={`https://grids.matchmovemachine.com/library/product/${singleProduct.slug}`} />
          <meta property="og:type" content="product" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={metadata.title} />
          <meta name="twitter:description" content={metadata.description} />
          <meta name="twitter:image" content="https://grids.matchmovemachine.com/preview.jpg" />
        </>}
      </Helmet>

      {isSuccess && <h1 className="visually-hidden">{productTitle}</h1>}

      {/* Hero — always dark, never unmounts during loading→success transition */}
      <div
        className={styles.headerSection}
        ref={headerSectionRef}
        onPointerMove={isSuccess ? handleHeroPointerMove : undefined}
        onPointerLeave={isSuccess ? handleHeroPointerLeave : undefined}
      >
        {isSuccess && singleProduct.preview_url && (
          <div className={styles.bgClip}>
            <div className={styles.bgLayer} aria-hidden="true" ref={bgLayerRef}>
              <img src={toAbsoluteUrl(singleProduct.preview_url)} alt="" className={styles.bgImage} />
            </div>
          </div>
        )}

        <div className={styles.heroBreadcrumbs}>
          <div className={styles.heroBreadcrumbsInner}>
            {isLoading
              ? <div className={`${styles.skeletonBlock} ${styles.skeletonCrumb}`} />
              : <div className={styles.crumbReveal}>
                  <ProductBreadcrumbs singleProduct={singleProduct} returnTo={returnTo} />
                </div>
            }
          </div>
        </div>

        <div className={`${styles.contentWrapper} width`}>
          <div className={styles.heroContent}>
            {isLoading ? (
              <div className={styles.skeletonContent}>
                <div className={`${styles.skeletonBlock} ${styles.skeletonTitle}`} />
                <div className={`${styles.skeletonBlock} ${styles.skeletonSub}`} />
                <div className={`${styles.skeletonBlock} ${styles.skeletonPrice}`} />
              </div>
            ) : isSuccess ? (
              <div className={styles.heroReveal} key={singleProduct.id}>
                <ProductDescription
                  handleOpen={handleOpen}
                  singleProduct={singleProduct}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* Below-fold skeleton during loading */}
      {isLoading && (
        <>
          <div className={styles.skeletonAssets}>
            <div className={styles.skeletonTabs}>
              <div className={`${styles.skeletonBlock} ${styles.skeletonTab}`} />
              <div className={`${styles.skeletonBlock} ${styles.skeletonTab}`} />
            </div>
            <div className={styles.skeletonAssetsBody}>
              <div className={styles.skeletonSidebar}>
                {[80, 60, 100, 70, 90, 55, 75].map((w, i) => (
                  <div key={i} className={`${styles.skeletonBlock} ${styles.skeletonSidebarItem}`} style={{ width: `${w}%`, animationDelay: `${i * 0.05}s` }} />
                ))}
              </div>
              <div className={styles.skeletonFileList}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className={`${styles.skeletonBlock} ${styles.skeletonFileRow}`} style={{ animationDelay: `${i * 0.06}s` }} />
                ))}
              </div>
            </div>
          </div>
          <div className={styles.skeletonRelated} />
          <div className={styles.skeletonJoin} />
        </>
      )}

      {/* Below-fold — fades in when content is ready */}
      {isSuccess && (
        <div className={styles.belowFold}>
          <ProductField
            singleProduct={{ ...singleProduct, preview_url: toAbsoluteUrl(singleProduct.preview_url) }}
            handleButtonClick={handleButtonClick}
            activeButton={activeButton}
          />
          <div ref={relatedRevealRef} className={styles.revealSection}>
            <RelatedProducts />
          </div>
          <div ref={joinRevealRef} className={styles.revealSection}>
            <JoinCommunity />
          </div>
          <SharePage
            profile={singleProduct.author}
            productSlug={singleProduct.slug}
            productTitle={`Distortion grids pack for ${productTitle}`}
          />
          <Modal isOpen={isOpen} onClose={handleClose}>
            <ContacUs />
          </Modal>
        </div>
      )}
    </>
  );
}

export default Product
